import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../../store';
import { createApiClient, apiClient } from '../../../api/apiClient';
import { setToastNotification } from '../../../store/slices/standUpSlice';
import { fetchFavorites } from '../../../store/slices/standupRedirectSlice';
import { useStandUpDashboard } from './useStandUpDashBoard';
import { handleStandUpErrorWithFriendlyMessage } from '../../../store/utils/standUpErrorHandler';
import { STANDUP_API_NAMES } from '../../../store/slices/standUpSlice';

/**
 * Map card title to item_id for API
 * @param title - The card title
 * @returns The mapped item_id
 */
const getTitleToItemIdMapping = (title: string): string => {
  const titleMap: { [key: string]: string } = {
    'Pulse Check': 'pulse_check',
    'Observation': 'observation',
    'Shift Handover': 'shift_handover',
    'Sparc Walk': 'sparc_walk',
    'Leadership Walk': 'leadership_walk',
  };
  
  return titleMap[title] || title.toLowerCase().replace(/\s+/g, '_');
};

/**
 * Custom hook to handle Lighthouse KPI favorite functionality
 * 
 * This hook provides functionality to:
 * - Toggle favorite status for Lighthouse KPIs
 * - Make POST request to /stand-up/favourites endpoint for adding
 * - Make PUT request to /stand-up/favourites endpoint for removing
 * 
 * @returns {object} Object containing toggleFavorite function and loading state
 */
export const useLighthouseKpiFavorite = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [isLoading, setIsLoading] = useState(false);

  // Get data from Redux
  const sessionId = useSelector((state: RootState) => state.standupRedirect?.session?.sessionId);
  const standupType = useSelector((state: RootState) => state.standupRedirect?.session?.standupType);
  const locationId = useSelector((state: RootState) => state.appData?.selectedLocation?.id);
  const locationName = useSelector((state: RootState) => state.appData?.selectedLocation?.displayText);
  const userName = useSelector(
    (state: RootState) =>
      state.appData?.userInfo?.email ?? state.auth?.user?.email ?? ''
  );
  const favoritesData = useSelector((state: RootState) => state.standupRedirect?.favorites?.data);
  const standupId = useSelector((state: RootState) => state.standupRedirect?.session?.standupId);
  const isEditMode = useSelector((state: RootState) => state.standupRedirect?.session?.isEditMode ?? false);
  const isViewMode = useSelector((state: RootState) => state.standupRedirect?.session?.isViewMode ?? false);

  const {transformStandupTypeForFavourites} = useStandUpDashboard();
  /**
   * Toggle favorite status for a Lighthouse KPI
   * 
   * @param title - The title of the Lighthouse KPI (used to derive item_id)
   * @returns Promise<boolean> - Returns true if successful, false otherwise
   */
  const toggleFavorite = async (title: string): Promise<boolean> => {
    // Validate required data
    if (!sessionId || !standupType || !locationId || !locationName || !userName) {
      console.error('Missing required data for favorite API call', {
        sessionId,
        standupType,
        locationId,
        locationName,
        userName,
      });
      return false;
    }

    setIsLoading(true);
    let currentOperation: 'add' | 'remove' = 'add'; // Track the current operation for error handling
    try {
      const api = createApiClient();
      const itemId = getTitleToItemIdMapping(title);

      // Extract the actual favorites array from the nested structure
      const favoritesArray = Array.isArray(favoritesData) 
        ? favoritesData 
        : (favoritesData as any)?.data || [];

      // Check if this item is already favorited
      const existingFavorite = favoritesArray.find(
        (fav: any) => fav.item_type === 'lighthouse_metric' && fav.item_id === itemId
      );

      if (existingFavorite) {
        // Item is already favorited - unfavorite it (PUT request)
        currentOperation = 'remove'; // Set operation type for error handling
        const requestBody =  isEditMode ? {
          standup_id: String(standupId),
          standup_session_id: String(sessionId),
          location_id: String(locationId),
          location_name: String(locationName),
          standup_type: transformStandupTypeForFavourites(String(standupType)) || '',
          item_type: 'lighthouse_metric',
          item_id: itemId,
          item_name: String(title),
          module_name: String(title),
          is_favourite: false,
          modified_by: String(userName),
        }:
        {
          standup_session_id: String(sessionId),
          location_id: String(locationId),
          location_name: String(locationName),
          standup_type: transformStandupTypeForFavourites(String(standupType)) || '',
          item_type: 'lighthouse_metric',
          item_id: itemId,
          item_name: String(title),
          module_name: String(title),
          is_favourite: false,
          modified_by: String(userName),
        };

        console.log('PUT /stand-up/favourites to unfavorite Lighthouse KPI with body:', requestBody);

        const response = await apiClient(api, '/stand-up/favourites', 'PUT', requestBody);

        if (response?.success) {
          console.log('Successfully removed Lighthouse KPI from favorites');
          
          // Fetch updated favorites list and store in Redux
          try {
            console.log('Fetching updated favorites after removing Lighthouse KPI...');
            await dispatch(
              fetchFavorites({
                standupSessionId: String(sessionId),
                standupId: String(standupId),
                locationId: String(locationId),
                locationName: String(locationName),
                standupType: transformStandupTypeForFavourites(String(standupType)),
                isEditMode: Boolean(isEditMode),
                isViewMode: Boolean(isViewMode),
              })
            ).unwrap();
            console.log('✓ Successfully fetched updated favorites');
          } catch (fetchError) {
            console.error('Error fetching updated favorites:', fetchError);
            // Don't fail the operation if fetching favorites fails
          }
          
          return true;
        } else {
          console.error('Failed to remove Lighthouse KPI from favorites:', response);
          // Use centralized error handler to show overlay/modal
          handleStandUpErrorWithFriendlyMessage(
            STANDUP_API_NAMES.REMOVE_FAVORITE,
            new Error(response?.message || 'Unable to remove favorite'),
            true, // displayModal = true → show overlay
            { itemId, title, action: 'remove_favorite' }
          );
          return false;
        }
      } else {
        // Item is not favorited - add it (POST request)
        currentOperation = 'add'; // Set operation type for error handling
        const requestBody = isEditMode ? {
          standup_id: String(standupId),
          standup_session_id: String(sessionId),
          location_id: String(locationId),
          location_name: String(locationName),
          standup_type: transformStandupTypeForFavourites(String(standupType)) || '',
          item_type: 'lighthouse_metric',
          item_id: itemId,
          item_name: String(title),
          created_by: String(userName),
        }:{
          standup_session_id: String(sessionId),
          location_id: String(locationId),
          location_name: String(locationName),
          standup_type: transformStandupTypeForFavourites(String(standupType)) || '',
          item_type: 'lighthouse_metric',
          item_id: itemId,
          item_name: String(title),
          created_by: String(userName),
        };

        console.log('POST /stand-up/favourites to add Lighthouse KPI with body:', requestBody);

        const response = await apiClient(api, '/stand-up/favourites', 'POST', requestBody);

        if (response?.success) {
          console.log('Successfully added Lighthouse KPI to favorites');
          
          // Fetch updated favorites list and store in Redux
          try {
            console.log('Fetching updated favorites after adding Lighthouse KPI...');
            await dispatch(
              fetchFavorites({
                standupSessionId: String(sessionId),
                standupId: String(standupId),
                locationId: String(locationId),
                locationName: String(locationName),
                standupType: transformStandupTypeForFavourites(String(standupType)) || '',
                isEditMode: Boolean(isEditMode),
                isViewMode: Boolean(isViewMode),
              })
            ).unwrap();
            console.log('✓ Successfully fetched updated favorites');
          } catch (fetchError) {
            console.error('Error fetching updated favorites:', fetchError);
            // Don't fail the operation if fetching favorites fails
          }
          
          return true;
        } else {
          console.error('Failed to add Lighthouse KPI to favorites:', response);
          // Use centralized error handler to show overlay/modal
          handleStandUpErrorWithFriendlyMessage(
            STANDUP_API_NAMES.ADD_FAVORITE,
            new Error(response?.message || 'Unable to add to favorites'),
            true, // displayModal = true → show overlay
            { itemId, title, action: 'add_favorite' }
          );
          return false;
        }
      }
    } catch (error) {
      console.error('Error toggling Lighthouse KPI favorite:', error);
      // Use the tracked operation type to show correct error message
      const apiName = currentOperation === 'remove' ? STANDUP_API_NAMES.REMOVE_FAVORITE : STANDUP_API_NAMES.ADD_FAVORITE;
      
      // Use centralized error handler for network/unexpected errors
      handleStandUpErrorWithFriendlyMessage(
        apiName,
        error,
        true, // displayModal = true → show overlay
        { title, action: `${currentOperation}_favorite` }
      );
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    toggleFavorite,
    isLoading,
  };
};
